import React from 'react';
import {
    Badge,
    Button,
    FormGroup,
    Progress,
} from 'reactstrap';
import {
    faAngleDoubleRight,
    faComments, faTasks,
    faUndo,
    faEye,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SlideForm from '../common/forms/SlideForm';
import BillingIssuesWidget from '../widgets/BillingIssuesWidget';
import CommonContext, { ApiRoutes, ApplicationPermissions, CustomFormContext } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    SimpleTable,
    FormCheckbox,
    SmallButton,
    PageHeading,
    PageWrap,
    toasty,
    FormLabel,
    onFieldChange,
    FlexCenterRow,
    Textarea,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import InvoiceSlideout from '../invoice/InvoiceSlideout';
import CustomForms from '../customForm/CustomForms';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { BillingStatus } from '../billing/Billing';
import { ModalForm } from '../common/forms/ModalForm';
import { Modal } from '../common/Modal';
import { MoveContractCompatibilityAlert } from '../common/MoveContractCompatibilityAlert';
import './InvoiceGenerationIndex.scss';

// Used to load from cache for the list types.
const getInitialListFiltersFromCache = (initialFilters) => {
    const json = sessionStorage.getItem('billing_gridFilterSettings');

    if (json) {
        const obj = JSON.parse(json);

        initialFilters.dispatchId = obj.DispatchLocationId
            ? obj.DispatchLocationId.value
            : '';
        initialFilters.hasOverride = obj.HasOverride
            ? obj.HasOverride.value
            : '';
        initialFilters.companyId = obj.CustomerCompanyId
            ? parseInt(obj.CustomerCompanyId.value)
            : 0;
        initialFilters.operationsCenterId = obj.OperationsCenterId
            ? parseInt(obj.OperationsCenterId.value)
            : 0;
        initialFilters.billingStatusId = obj.BillingStatusId
            ? parseInt(obj.BillingStatusId.value)
            : 0;
        initialFilters.payrollStatusId = obj[
            'Timesheet.PayrollStatus.Description'
        ]
            ? parseInt(obj['Timesheet.PayrollStatus.Description'].value)
            : 0;
    }

    return initialFilters;
};

const printTimesheet = (id) => {
    if (id) {
        httpClient.open(ApiRoutes.report.timesheetBilling(id), '_self');
    }
};

class InvoiceGenerationIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();
        this.invoiceSlideoutRef = React.createRef();
        this.billingIssuesSlideoutRef = React.createRef();
        this.customFormSlideoutRef = React.createRef();

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            timesheetDetailsOpen: false,
            invoiceSlideoutOpen: false,
            selectedDetails: [],
            showRejectBillingTimesheetModal: false,
            showMoveTimesheetModal: false,
            isMovingTimesheet: false,
            contractMoveBlocked: false,
            allTimesheetsByJob: [],
            isRejectingBillingTimesheet: false,
            adminRejecting: false,
            selectedRowRejectionNotes: '',
            loadingReport: false,
            yesNo,
            isApproving: false,
            isInvoicing: false,
            createJob: false,
            showBillingIssuesSlideout: false,
            billingIssueMessages: [],
            filterModel: null,
            invoicedTimesheetsSelected: false,
            scheduledTimesheetSelected: false,
            quickSaleRowSelected: false,
            selectedFormRow: null,
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAcceptBillingIssueReply = async (id, message) => {
        const model = {
            timesheetId: id,
            notes: message,
        };

        await httpClient.post(ApiRoutes.billingIssues.accept(), model);

        toasty.success('Billing reply accepted');

        this.setState({
            showBillingIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    onBillingIssuesSlideout = async (id) => {
        const messages = await httpClient.js(
            ApiRoutes.billingIssues.messages(id),
        );

        this.setState({
            billingIssueMessages: messages,
            showBillingIssuesSlideout: true,
        });
    };

    onChange = onFieldChange;

    onGenerateInvoice = async () => {
        this.setState({ isInvoicing: true });

        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            // check if all the selected TS are from same contract
            const contractIds = rowsSelected.map((x) => x.contractId);
            const distinctContractIds = [...new Set(contractIds)];
            const workOrderNumbers = rowsSelected.map((x) => x.workOrderNumber?.toUpperCase());
            const invoiced = rowsSelected.filter((x) => x.billingStatus == 'Invoiced');

            const distinctWorkOrderNumbers = [...new Set(workOrderNumbers)];

            if (invoiced.length > 0) {
                toasty.error(
                    'Selections contain timesheets that are already invoiced.',
                );
                this.setState({ isInvoicing: false });
            } else if (distinctContractIds.length !== 1) {
                // throw error
                toasty.error(
                    'Selected timesheets are not from the same contract.',
                );
                this.setState({ isInvoicing: false });
            } else {
                const [{ allowMultipleWONumbers }] = rowsSelected;

                if (
                    distinctWorkOrderNumbers.length !== 1
                    && !allowMultipleWONumbers
                ) {
                    // throw error
                    toasty.error(
                        'Selected timesheets are not from the same work order.',
                    );
                    this.setState({ isInvoicing: false });
                } else {
                    await this.context.setFormOpened(true);
                    // get rows ids
                    const model = {
                        billingIds: rowsSelected.map((x) => x.id),
                        invoiceId: null,
                    };
                    this.invoiceSlideoutRef.current.open(model);
                }
            }
        }
    };

    onInvoiceChargeReport = async () => {
        const { rowsSelected } = this.state;

        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id);

            await httpClient
                .downloadFile(
                    ApiRoutes.invoiceGeneration.generateChargeReport(),
                    model,
                    'InvoiceChargeReport.xlsx',
                )
                .catch(this.handleSaveError);
        }
    };

    onFormSaved = () => {
        this.state.gridOptions.refresh();
    };

    onInvoiceSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ rowsSelected: [], isInvoicing: false });
        this.state.gridOptions.refresh();
    };

    onRejectToBillingIssues = async (id, message, updateStatus) => {
        if (message == null || message.length == 0) {
            toasty.error('Please supply rejection notes');
            return;
        }

        const model = {
            timesheetId: id,
            notes: message,
            updateStatus,
        };

        await httpClient.post(ApiRoutes.billingIssues.reject(), model);

        toasty.success('Billing row rejected to Billing Issues');

        this.setState({
            showBillingIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
            // this.openTimesheetDetails();
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        let { invoicedTimesheetsSelected, scheduledTimesheetSelected, quickSaleRowSelected } = this.state;
        const rs = e.api.getSelectedRows();

        const invoicedItems = rs.filter((x) => x.billingStatus == 'Invoiced');
        const scheduledItems = rs.filter((x) => x.invoicingScheduled == true);
        const quickSaleItems = rs.filter((x) => x.isQuickSale === true);
        invoicedTimesheetsSelected = invoicedItems.length > 0;
        scheduledTimesheetSelected = scheduledItems.length > 0;
        quickSaleRowSelected = quickSaleItems.length > 0;

        this.setState({
            rowsSelected: rs,
            invoicedTimesheetsSelected,
            scheduledTimesheetSelected,
            quickSaleRowSelected,
        });
    }

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    setOnHold = async (id) => {
        if (id) {
            const response = await httpClient
                .post(ApiRoutes.invoiceGeneration.setOnHold(), id)
                .catch(this.handleSaveError);

            if (response) {
                toasty.error(`Error setting on hold: ${response}`);
            } else {
                toasty.success('Hold set');
            }

            this.state.gridOptions.refresh();
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    onContractMoveBlockedChange = (contractMoveBlocked) => this.setState({ contractMoveBlocked });

    moveTimesheets = async () => {
        const { selectedContract, allTimesheetsByJob } = this.state;

        this.setState({ isMovingTimesheet: true });

        const model = {
            contractId: selectedContract,
            timesheetIds: allTimesheetsByJob
                .filter((x) => x.convert == true)
                .map((e) => e.id),
        };

        try {
            const response = await httpClient.post(
                ApiRoutes.contract.moveTimesheets(),
                model,
            );

            if (response.result == 0) {
                toasty.success('Timesheets moved');
            } else {
                toasty.error('Error moving timesheets');
            }
        } catch (err) {
            toasty.error(err?.handledError?.message ?? 'Error moving timesheets');
        }

        this.setState({
            isMovingTimesheet: false,
            showMoveTimesheetModal: false,
        });

        this.state.gridOptions.refresh();
    };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        let [dispatchLocations, billingStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.billingStatuses()),
        ]);

        const canMoveTimesheetContract = this.context.checkUserHasPermission(ApplicationPermissions.billing_tscontract_update)
            && !this.context.tenant.tenantSettings.enforceContractUnionWork;

        let initialListFilters = {
            initialDispatchId: '',
            initialCustomerCompanyId: 0,
            initialOperationsCenterId: 0,
            initialBillingStatusId: 6,
            initialPayrollStatusId: 0,
            initialHasOverride: null,
        };

        billingStatuses = billingStatuses.filter(
            (x) => x.value == 6
                || x.value == 4
                || x.value == 8
                || x.value == 10
                || x.value == 9,
        );
        initialListFilters = getInitialListFiltersFromCache(initialListFilters);

        const gridOptions = createGridOptions(this);
        const context = this;

        // https://www.ag-grid.com/documentation/javascript/row-selections#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.rowClassRules = {
            'ag-row-warning': (params) => (params.data ?? {}).hasOverride === 'Yes',
            'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
            'ag-row-otjobstart': (params) => (params.data ?? {}).hasDT === true,
            'ag-row-invoicingschedule': (params) => (params.data ?? {}).invoicingScheduled === true,
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };
        gridOptions.onRowClicked = this.onRowClicked;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.dispatchId,
        };
        const billingStatusFilterParams = {
            suppressFilterButton: true,
            options: [...billingStatuses, { label: 'Scheduled', value: -1 }],
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.initialBillingStatusId,
        };
        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: this.state.yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: initialListFilters.hasOverride,
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                // floatingFilterComponentParams: {
                //    suppressFilterButton: true,
                // }
            },
            {
                colId: 'customerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'endCustomer',
                headerName: 'End Customer',
                field: 'endCustomer',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                headerName: 'Contract #',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'operationsCenterName',
                headerName: 'Operations Center',
                field: 'operationsCenterName',
                sortable: false,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchLocationId',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'Timesheet.TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Timesheet.WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobLocationAddress',
                headerName: 'Job Address',
                field: 'jobAddress',
                sortable: false,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                headerName: 'Flags',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Foreman',
                headerName: 'Foreman',
                field: 'foreman',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'startTime',
                headerName: 'Start Time',
                field: 'startTime',
                sortable: false,
                flex: 1,
            },
            {
                colId: 'Invoice.InvoiceNumber',
                headerName: 'Invoice #',
                field: 'invoiceNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'BillingStatusId',
                headerName: 'Status',
                field: 'billingStatus',
                sortable: true,
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billingStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billingStatusFilterParams,
            },
            {
                colId: 'HasOverride',
                headerName: 'Billing Override',
                field: 'hasOverride',
                sortable: true,
                flex: 1.5,
                tooltipField: 'status',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'Billing Issue',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        return (
                            <SmallButton
                                title="Reject"
                                type="button"
                                onClick={() => {
                                    this.onBillingIssuesSlideout(
                                        params.data.timesheetId,
                                    );
                                }}
                            >
                                <i className="fa fa-comments fa-md mr-2" />
                            </SmallButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'Hold',
                sortable: false,
                cellRenderer: (params) => {
                    if (
                        params.data
                        && params.data.billingStatusId
                        !== BillingStatus.IssueReject
                        && params.data.billingStatusId
                        !== BillingStatus.PendingReview
                        && params.data.invoicingScheduled !== true
                    ) {
                        if (params.data.billingStatusId === 8) {
                            return (
                                <SmallButton
                                    title="Hold"
                                    type="button"
                                    onClick={() => {
                                        this.removeOnHold(params.data.id);
                                    }}
                                >
                                    <i className="fa fa-unlock fa-md mr-2" />
                                </SmallButton>
                            );
                        }
                        return (
                            <SmallButton
                                title="Hold"
                                type="button"
                                onClick={() => {
                                    this.setOnHold(params.data.id);
                                }}
                            >
                                <i className="fa fa-lock fa-md mr-2" />
                            </SmallButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'View',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data && params.data.isQuickSale === true) {
                        return null;
                    }
                    return (
                        <SmallButton
                            title="View"
                            type="button"
                            onClick={() => {
                                this.openTimesheetDetails(params.data.timesheetId);
                            }}
                        >
                            <i className="fa fa-eye fa-md mr-2" />
                        </SmallButton>
                    );
                },
            },
            {
                colId: 'forms',
                flex: 0,
                maxWidth: 80,
                headerName: 'Forms',
                sortable: false,
                cellRenderer: (params) => {
                    if (!params.data || !params.data.hasCustomForms) return null;
                    return (
                        <SmallButton
                            type="button"
                            onClick={() => {
                                this.setState({ selectedFormRow: params.data }, () => {
                                    this.customFormSlideoutRef.current?.open();
                                });
                            }}
                        >
                            <FontAwesomeIcon
                                icon={params.data.formsCompleted ? faEye : faExclamationTriangle}
                                className={params.data.formsCompleted ? '' : 'text-danger'}
                            />
                        </SmallButton>
                    );
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'Print',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: printTimesheet,
                    idField: 'timesheetId',
                    iconClass: 'fa-print',
                },
            },
            {
                colId: 'SelectionPlaceholder',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                maxWidth: 75,
                checkboxSelection(params) {
                    if (params.data) {
                        return params.data.billingStatus === 'Ready To Invoice';
                    }

                    return null;
                },
            },
        ];

        if (this.context?.user?.isAdmin) {
            const overrideIndex = gridOptions.columnDefs.findIndex((c) => c.colId === 'HasOverride');
            const col = {
                colId: 'invoiceEstimateAmount',
                headerName: 'Invoice Est.',
                field: 'invoiceEstimateAmount',
                sortable: false,
                flex: 1,
            };
            if (overrideIndex) {
                gridOptions.columnDefs.splice(overrideIndex + 1, 0, col);
            } else {
                gridOptions.columnDefs.push(col);
            }
        }

        gridOptions.onFilterChanged = () => {
            const filterModel = gridOptions.api.getFilterModel();
            context.setState({ filterModel });
        };

        gridOptions.onSortChanged = () => { };

        gridOptions.onFirstDataRendered = () => { };

        gridOptions.postProcessData = (data) => {
            context.setState({
                rowData: data,
                scoreboardData: { ...data.counts } ?? null,
            });

            return { ...data.result };
        };

        gridOptions.onGridReady = (params) => {
            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        const dataSource = createDataSource(
            ApiRoutes.invoiceGeneration.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            canMoveTimesheetContract,
        });
    };

    rejectBillingTimesheet = async ({ selectedRowRejectionNotes }) => {
        const { rowsSelected } = this.state;

        if (
            selectedRowRejectionNotes.length === 0
            || !selectedRowRejectionNotes.trim()
        ) return;

        this.setState({ isRejectingBillingTimesheet: true });

        // call reject
        if (rowsSelected) {
            const model = {
                billingIds: rowsSelected.map((x) => x.id),
                note: selectedRowRejectionNotes,
            };

            const endPoint = ApiRoutes.invoiceGeneration.billingReject();
            const response = await httpClient
                .post(endPoint, model)
                .catch(this.handleSaveError);

            if (response) {
                toasty.success(
                    'All charges for the selected timesheets will be rejected to Billing: Charges',
                );
            }
        }

        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            showRejectBillingTimesheetModal: false,
            selectedRowRejectionNotes: '',
            isRejectingBillingTimesheet: false,
        });
    };

    removeOnHold = async (id) => {
        if (id) {
            const response = await httpClient
                .post(ApiRoutes.invoiceGeneration.removeOnHold(), id)
                .catch(this.handleSaveError);

            if (response) {
                toasty.error(`Error setting removing on hold: ${response}`);
            } else {
                toasty.success('Hold removed');
            }

            this.state.gridOptions.refresh();
        }
    };

    toggleMovingTimesheetModal = async () => {
        const { rowsSelected, showMoveTimesheetModal } = this.state;
        let contracts = [];
        let allTimesheetsByJob = [];

        if (!showMoveTimesheetModal) {
            if (rowsSelected) {
                const jobNums = rowsSelected.map((e) => {
                    const p = e.timesheetNumber.split('-');
                    return `${p[0]}-${p[1]}`;
                });

                const distinctjobs = [...new Set(jobNums)];

                if (distinctjobs.length !== 1) {
                    // throw error
                    toasty.error(
                        'Selected timesheets are not from the same job.',
                    );
                    return;
                }

                // Get contracts based on company
                contracts = await httpClient.js(
                    ApiRoutes.typeAheads.contractsByCompany(
                        rowsSelected[0].customerId,
                    ),
                );

                contracts = contracts.map((x) => ({
                    label:
                        x.number
                        + (x.operationsCenterName != null
                            ? ` (${x.operationsCenterName})`
                            : ''),
                    value: x.id,
                }));

                // Get all timesheets belong to jobs involved and display them
                const timesheetIds = rowsSelected.map((x) => x.timesheetId);

                const timesheetList = timesheetIds.join(',');
                allTimesheetsByJob = await httpClient.js(
                    ApiRoutes.timesheetManagement.timesheetsBelongingToJobs(
                        timesheetList,
                    ),
                );
            }
        }

        this.setState({
            showMoveTimesheetModal: !showMoveTimesheetModal,
            contracts,
            allTimesheetsByJob,
            selectedContract: 0,
        });
    };

    toggleRejectBillingTimesheetModal = () => {
        Modal.show({
            title: 'Reject Timesheet to Billing: Charges',
            onSubmit: (values) => this.rejectBillingTimesheet(values),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: () => (
                <>
                    <div
                        style={{ marginBottom: '20px' }}
                        className="form-text text-danger"
                    >
                        This action will reset all charges to
                        associated with these timesheets back to
                        Billing: Charges.
                    </div>
                    <Textarea
                        label="Notes"
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        maxLength="500"
                        rows="5"
                        error="Notes are required."
                    />
                </>
            ),
        });
    };

    renderTimesheetByjobList = () => {
        const { allTimesheetsByJob } = this.state;

        const groupContainsInvoice = allTimesheetsByJob.filter((e) => e.invoiceStatusId > 0).length > 0;
        const groupMixSelected = allTimesheetsByJob.filter((e) => e.convert == false).length > 0;

        return allTimesheetsByJob.map((timesheet) => {
            const tsHasInvoice = timesheet.invoiceStatusId > 0;

            return (
                <tr key={allTimesheetsByJob}>
                    <td>
                        {tsHasInvoice && (
                            <FormCheckbox
                                id={timesheet.id}
                                checked={false}
                                labelText={timesheet.timesheetNumber}
                                disabled
                            />
                        )}
                        {!tsHasInvoice && (
                            <FormCheckbox
                                id={timesheet.id}
                                checked={timesheet.convert}
                                onChange={(e) => {
                                    const { allTimesheetsByJob } = this.state;
                                    timesheet.convert = e.target.checked;
                                    this.setState({
                                        allTimesheetsByJob,
                                    });
                                }}
                                labelText={timesheet.timesheetNumber}
                            />
                        )}
                    </td>
                    <td>
                        {timesheet.convert && (
                            <>
                                {!tsHasInvoice
                                    && (groupContainsInvoice
                                        || groupMixSelected) && (
                                        <span>
                                            <ul
                                                style={{
                                                    paddingLeft: '18px',
                                                    marginBottom: '0px',
                                                }}
                                            >
                                                <li>New job</li>
                                                <li>New timesheet number</li>
                                            </ul>
                                        </span>
                                    )}
                                {!tsHasInvoice
                                    && !groupContainsInvoice
                                    && !groupMixSelected && (
                                        <span>
                                            <ul
                                                style={{
                                                    paddingLeft: '18px',
                                                    marginBottom: '0px',
                                                }}
                                            >
                                                <li>Same job</li>
                                                <li>New timesheet number</li>
                                            </ul>
                                        </span>
                                    )}
                            </>
                        )}
                        {!timesheet.convert && (
                            <>
                                {tsHasInvoice && <span>(unavailable)</span>}
                                {!tsHasInvoice && <span>(none)</span>}
                            </>
                        )}
                    </td>
                    <td>{timesheet.timesheetStatus}</td>
                    <td>{timesheet.billingStatus}</td>
                    <td>{timesheet.invoiceStatus}</td>
                </tr>
            );
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            filterModel,
            showMoveTimesheetModal,
            isRejectingBillingTimesheet,
            isMovingTimesheet,
            contractMoveBlocked,
            isInvoicing,
            rowsSelected,
            contracts,
            allTimesheetsByJob,
            selectedContract,
            selectedRow,
            billingIssueMessages,
            scoreboardData,
            canMoveTimesheetContract,
            invoicedTimesheetsSelected,
            scheduledTimesheetSelected,
            quickSaleRowSelected,
        } = this.state;

        let counts = {
            onHold: 0,
            pendingReview: 0,
            billingIssue: 0,
            invoiceEstimate: 0,
        };
        if (scoreboardData) {
            counts = scoreboardData;
        }

        const countOnHoldColor = counts.onHold === 0 ? 'success' : 'warning';
        const countPendingReviewColor = counts.pendingReview === 0 ? 'success' : 'warning';
        const countBillingIssueColor = counts.billingIssue === 0 ? 'success' : 'warning';
        const countInvoiceEstimateColor = counts.invoiceEstimate === 0 ? 'success' : 'warning';

        let billingFilter = BillingStatus.ReadyToInvoice;
        if (filterModel != null) {
            billingFilter = filterModel?.BillingStatusId?.value != null
                ? filterModel.BillingStatusId.value
                : billingFilter;
        }

        return (
            <CommonContext.Consumer>
                {(value) => {
                    const { tenantSettings } = (value ?? {}).tenant ?? {};

                    if (this.state.loading || !tenantSettings) {
                        return (
                            <Progress />
                        );
                    }

                    if (
                        !tenantSettings.billingEnabled
                        || !tenantSettings.invoicingEnabled
                    ) return <NotAuthorizedPage />;

                    const isAdmin = this.context?.user?.isAdmin;

                    return (
                        <PageWrap>
                            <PageHeading>
                                <FontAwesomeIcon
                                    icon={faTasks}
                                    className="mr-2 text-muted"
                                />
                                <span>Invoice Generation</span>

                                {billingFilter != BillingStatus.IssueReject
                                    && billingFilter
                                    != BillingStatus.PendingReview
                                    && billingFilter
                                    != BillingStatus.Invoiced
                                    && (
                                        <span
                                            style={{
                                                float: 'right',
                                                position: 'relative',
                                                top: '-5px',
                                            }}
                                        >
                                            {rowsSelected.length > 0 && !invoicedTimesheetsSelected && !scheduledTimesheetSelected && (
                                                <Button
                                                    size="sm"
                                                    color="success"
                                                    style={{
                                                        marginRight: '10px',
                                                    }}
                                                    onClick={() => this.onGenerateInvoice()}
                                                    disabled={
                                                        rowsSelected.length
                                                        === 0 || isInvoicing
                                                    }
                                                >
                                                    Generate Invoice
                                                </Button>
                                            )}
                                            {rowsSelected.length > 0 && !invoicedTimesheetsSelected && !quickSaleRowSelected && (
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    style={{
                                                        marginRight: '10px',
                                                    }}
                                                    onClick={() => this.toggleRejectBillingTimesheetModal()}
                                                    disabled={
                                                        rowsSelected.length
                                                        === 0
                                                        || isRejectingBillingTimesheet
                                                    }
                                                >
                                                    Reject To Billing Charges
                                                </Button>
                                            )}
                                            {rowsSelected.length > 0 && !invoicedTimesheetsSelected && !scheduledTimesheetSelected && !quickSaleRowSelected && (
                                                <Button
                                                    size="sm"
                                                    color="info"
                                                    style={{
                                                        marginRight: '10px',
                                                    }}
                                                    onClick={() => this.onInvoiceChargeReport()}
                                                >
                                                    Invoice Charge Report
                                                </Button>
                                            )}

                                            {canMoveTimesheetContract
                                                && rowsSelected.length > 0
                                                && !invoicedTimesheetsSelected
                                                && !scheduledTimesheetSelected
                                                && !quickSaleRowSelected
                                                && (
                                                    <Button
                                                        size="sm"
                                                        color="warning"
                                                        onClick={() => this.toggleMovingTimesheetModal()}
                                                        disabled={
                                                            rowsSelected.length
                                                            == 0
                                                            || isMovingTimesheet
                                                        }
                                                    >
                                                        Move Timesheet OPS Center
                                                    </Button>
                                                )}
                                        </span>
                                    )}
                            </PageHeading>

                            <FlexCenterRow
                                className="pb-3 pt-2"
                                id="invoiceGenerationWorkflowStatus"
                            >
                                <Badge
                                    color={countOnHoldColor}
                                    style={{ minWidth: '100px' }}
                                    className="mr-3 p-2"
                                >
                                    On Hold
                                    <span className="ml-2">
                                        {counts.onHold}
                                    </span>
                                </Badge>
                                <Badge
                                    color={countBillingIssueColor}
                                    style={{ minWidth: '100px' }}
                                    className="mr-3 p-2"
                                >
                                    Billing Issue
                                    <span className="ml-2">
                                        {counts.billingIssue}
                                    </span>
                                </Badge>
                                <Badge
                                    color={countPendingReviewColor}
                                    style={{ minWidth: '100px' }}
                                    className="mr-3 p-2"
                                >
                                    Pending Review
                                    <span className="ml-2">
                                        {counts.pendingReview}
                                    </span>
                                </Badge>
                                {isAdmin && (
                                    <Badge
                                        color={countInvoiceEstimateColor}
                                        style={{ minWidth: '100px' }}
                                        className="mr-3 p-2"
                                    >
                                        Invoices Estimate
                                        <span className="ml-2">
                                            ${counts.invoiceEstimate.toFixed(2)}
                                        </span>
                                    </Badge>
                                )}
                            </FlexCenterRow>

                            <DataGridToolbar
                                entity="Billing"
                                gridApi={this.state.gridApi}
                                dataSource={this.state.dataSource}
                                hideAdd
                                hideExcelButton
                                gridOptions={this.state.gridOptions}
                                serverExport={{
                                    apiPath:
                                        ApiRoutes.invoiceGeneration.excelExport(),
                                    filePrefix: 'BillingSearch',
                                }}
                                serverExportDisabled={
                                    !!this.state.saving
                                    || !!this.state.loading
                                    || !!this.state.loadingData
                                }
                            />
                            {!!this.state.loadingReport && (
                                <CustomCircularProgress />
                            )}

                            {!this.state.loadingReport && (
                                <DataGrid
                                    domLayout="normal"
                                    rowData={rowData}
                                    gridOptions={gridOptions}
                                    gridStatus={this.state.gridStatus}
                                />
                            )}
                            <TimesheetDetails
                                ref={this.timesheetDetailsRef}
                                show={this.state.timesheetDetailsOpen}
                                toggleShow={(open) => this.setState({
                                    timesheetDetailsOpen: open,
                                })}
                                onClose={this.onTimesheetDetailsClosed}
                                onAccept={this.onAcceptTimesheet}
                                onlyBillable
                                isBilling
                            />
                            <InvoiceSlideout
                                ref={this.invoiceSlideoutRef}
                                show={this.state.invoiceSlideoutOpen}
                                toggleShow={(open) => this.setState({ invoiceSlideoutOpen: open })}
                                onClose={this.onInvoiceSlideoutClosed}
                            />
                            {this.state.selectedFormRow && (
                                <CustomForms
                                    ref={this.customFormSlideoutRef}
                                    showButton={false}
                                    formContextId={CustomFormContext.Invoice}
                                    selectedFormContextId={
                                        this.state.selectedFormRow.isQuickSale
                                            ? CustomFormContext.QuickSale
                                            : CustomFormContext.Job
                                    }
                                    invoice={{
                                        invoiceTimesheets: [{
                                            timesheetId: this.state.selectedFormRow.timesheetId,
                                        }],
                                    }}
                                    onSave={this.onFormSaved}
                                />
                            )}

                            <ModalForm
                                isOpen={showMoveTimesheetModal}
                                onSubmit={() => this.moveTimesheets()}
                                toggle={() => this.toggleMovingTimesheetModal()}
                                title="Move Timesheets to Contract / OPS Center"
                                size="lg"
                                SaveButtonProps={{
                                    label: 'Move',
                                    icon: faAngleDoubleRight,
                                    savingLabel: 'Moving',
                                    saving: isMovingTimesheet,
                                    disabled: selectedContract == null
                                        || selectedContract == 0
                                        || isMovingTimesheet
                                        || contractMoveBlocked
                                        || allTimesheetsByJob.filter(
                                            (x) => x.convert == true,
                                        ).length == 0,
                                }}
                            >
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="timesheetJobsTable"
                                        label="Job Timesheets"
                                    />

                                    <SimpleTable
                                        className="mb-0"
                                        id="timesheetJobsTable"
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={[
                                            {
                                                name: 'Timesheet Number',
                                            },
                                            {
                                                name: 'Action(s)',
                                            },
                                            {
                                                name: 'Timesheet Status',
                                            },
                                            {
                                                name: 'Billing Status ',
                                            },
                                            {
                                                name: 'Invoice Status ',
                                            },
                                        ]}
                                        editable={false}
                                        entities={allTimesheetsByJob}
                                        rowRenderer={
                                            this.renderTimesheetByjobList
                                        }
                                        noDataText="No timesheets found."
                                    />
                                </FormGroup>

                                <ValidatedSelect
                                    placeholder="Select Contract"
                                    id="selectedContract"
                                    name="selectedContract"
                                    label="Contract / OPS Center"
                                    isClearable={false}
                                    options={contracts}
                                    required
                                    error="A contract must be selected"
                                    value={
                                        (contracts ?? []).find(
                                            (x) => x.value
                                                === selectedContract,
                                        ) ?? ''
                                    }
                                    onChange={(evt) => {
                                        const { value } = evt;
                                        this.setState({
                                            selectedContract: value,
                                        });
                                    }}
                                />

                                <MoveContractCompatibilityAlert
                                    url={ApiRoutes.contract.validateMoveTimesheets()}
                                    model={{
                                        contractId: selectedContract,
                                        timesheetIds: allTimesheetsByJob.filter((x) => x.convert == true).map((e) => e.id),
                                    }}
                                    onBlockedChange={this.onContractMoveBlockedChange}
                                />
                            </ModalForm>

                            <SlideForm
                                loading={false}
                                show={this.state.showBillingIssuesSlideout}
                                id="billingIssuesSlideout"
                                formIcon={faComments}
                                formTitle="Billing Issues"
                                onClose={() => {
                                    this.setState({
                                        showBillingIssuesSlideout: false,
                                    });
                                }}
                            >
                                <BillingIssuesWidget
                                    ref={this.billingIssuesSlideoutRef}
                                    billing={selectedRow}
                                    messages={billingIssueMessages}
                                    context="rejecting"
                                    onRejectCallback={(id, message, updateStatus) => {
                                        this.onRejectToBillingIssues(
                                            id,
                                            message,
                                            updateStatus,
                                        );
                                    }}
                                    onAcceptCallback={(id, message) => {
                                        this.onAcceptBillingIssueReply(
                                            id,
                                            message,
                                        );
                                    }}
                                    onCloseCallback={() => {
                                        this.setState({
                                            showBillingIssuesSlideout: false,
                                        });
                                    }}
                                />
                            </SlideForm>
                        </PageWrap>
                    );
                }}
            </CommonContext.Consumer>
        );
    }
}

export default InvoiceGenerationIndex;
